import React from 'react';
import { Box } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { Notification } from '@/types';

type NotificationCategory =
  | 'sla'
  | 'ticket'
  | 'deal'
  | 'shift'
  | 'leave'
  | 'inquiry'
  | 'billing'
  | 'general';

interface NotificationVisual {
  category: NotificationCategory;
  label: string;
  color: string;
  background: string;
  Icon: typeof NotificationsNoneRoundedIcon;
}

const getNotificationVisual = (note: Notification): NotificationVisual => {
  const entity = note.entityType?.toLowerCase() ?? '';
  const type = note.type?.toLowerCase() ?? '';

  if (type.includes('sla')) {
    return {
      category: 'sla',
      label: 'SLA',
      color: '#B42318',
      background: '#FEE4E2',
      Icon: NotificationsActiveOutlinedIcon,
    };
  }

  if (type === 'ticket_assigned' || type === 'ticket_reassigned' || entity === 'ticket') {
    return {
      category: 'ticket',
      label: 'Ticket',
      color: '#0057B8',
      background: '#E6F0FF',
      Icon: ConfirmationNumberOutlinedIcon,
    };
  }

  if (type === 'deal_assigned' || type === 'deal_created' || entity === 'deal') {
    return {
      category: 'deal',
      label: 'Deal',
      color: '#027A48',
      background: '#E7F8EF',
      Icon: TrendingUpRoundedIcon,
    };
  }

  if (type.includes('shift_swap') || entity === 'shift_swap_request') {
    return {
      category: 'shift',
      label: 'Shift',
      color: '#6941C6',
      background: '#F0EAFE',
      Icon: SwapHorizRoundedIcon,
    };
  }

  if (type.includes('leave_request') || entity === 'leave_request') {
    return {
      category: 'leave',
      label: 'Leave',
      color: '#B54708',
      background: '#FEF0C7',
      Icon: EventAvailableOutlinedIcon,
    };
  }

  if (
    type === 'sales_inquiry_created'
    || type === 'query_inquiry_created'
    || type === 'customer_update_request'
    || entity === 'inquiry'
  ) {
    return {
      category: 'inquiry',
      label: 'Inquiry',
      color: '#087E8B',
      background: '#E5F8FA',
      Icon: ForumOutlinedIcon,
    };
  }

  if (
    type === 'invoice_success'
    || type === 'grace_period_start'
    || type === 'trial_reminder'
    || entity === 'subscriptioninvoice'
    || entity === 'usersubscription'
  ) {
    return {
      category: 'billing',
      label: 'Billing',
      color: '#C11574',
      background: '#FCE7F6',
      Icon: ReceiptLongOutlinedIcon,
    };
  }

  return {
    category: 'general',
    label: 'General',
    color: '#344054',
    background: '#EEF2F6',
    Icon: NotificationsNoneRoundedIcon,
  };
};

interface NotificationTypeIconProps {
  notification: Notification;
  size?: 'small' | 'medium';
}

const NotificationTypeIcon: React.FC<NotificationTypeIconProps> = ({
  notification,
  size = 'medium',
}) => {
  const visual = getNotificationVisual(notification);
  const dimension = size === 'small' ? 40 : 48;

  return (
    <Box
      role="img"
      aria-label={`${visual.label} notification`}
      data-testid={`notification-type-icon-${visual.category}`}
      sx={{
        alignItems: 'center',
        bgcolor: visual.background,
        borderRadius: '12px',
        color: visual.color,
        display: 'inline-flex',
        flex: '0 0 auto',
        height: dimension,
        justifyContent: 'center',
        width: dimension,
      }}
    >
      <visual.Icon sx={{ fontSize: size === 'small' ? 21 : 24 }} />
    </Box>
  );
};

export default NotificationTypeIcon;
